import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  MapPin,
  Users,
  Building,
  Activity,
  Plus,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  Phone,
  Mail
} from 'lucide-react';
import { useHealthZones } from '../../hooks/useApi';
import useHealthZonesCrud from '../../hooks/useHealthZonesCrud';
import HealthZoneDetailsModal from './HealthZoneDetailsModal';
import HealthZoneEditModal from './HealthZoneEditModal';
import HealthZoneDeleteModal from './HealthZoneDeleteModal';
import HealthZoneAddModal from './HealthZoneAddModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const HealthZonesList = () => {
  const { data: healthZones = [], loading, error, refetch } = useHealthZones();
  const { addHealthZone, updateHealthZone, deleteHealthZone } = useHealthZonesCrud();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // États des modales
  const [selectedHealthZone, setSelectedHealthZone] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Nettoyer les états lors du démontage du composant
  useEffect(() => {
    return () => {
      // Fermer toutes les modales lors du démontage
      setIsDetailsModalOpen(false);
      setIsEditModalOpen(false);
      setIsDeleteModalOpen(false);
      setIsAddModalOpen(false);
      setSelectedHealthZone(null);
    };
  }, []);

  const provinces = [...new Set(healthZones.map(zone => zone.province))];
  const statuses = ['Fonctionnelle', 'Partiellement fonctionnelle', 'Non fonctionnelle', 'En construction'];

  const filteredHealthZones = healthZones.filter(zone => {
    const name = zone.name || '';
    const code = zone.code || '';
    const province = zone.province || '';
    const territory = zone.territory || '';
    const chiefTown = zone.chiefTown || '';
    
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         province.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         territory.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chiefTown.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProvince = !filterProvince || province === filterProvince;
    const matchesStatus = !filterStatus || zone.status === filterStatus;
    return matchesSearch && matchesProvince && matchesStatus;
  });

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Titre
    doc.setFontSize(20);
    doc.text('Répertoire des Zones de Santé - RDC', 20, 20);
    
    // Tableau
    const tableData = filteredHealthZones.map(zone => [
      zone.name || 'N/A',
      zone.code || 'N/A',
      zone.province || 'N/A',
      zone.territory || 'N/A',
      zone.chiefTown || 'N/A',
      zone.population || '0',
      zone.status || 'N/A'
    ]);

    autoTable(doc, {
      head: [['Nom', 'Code', 'Province', 'Territoire', 'Chef-lieu', 'Population', 'Statut']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [59, 130, 246] }
    });

    doc.save('repertoire-zones-sante.pdf');
  };

  const handleViewDetails = (zone) => {
    setSelectedHealthZone(zone);
    setIsDetailsModalOpen(true);
  };

  const handleEditHealthZone = (zone) => {
    setSelectedHealthZone(zone);
    setIsEditModalOpen(true);
  };

  const handleDeleteHealthZone = (zone) => {
    setSelectedHealthZone(zone);
    setIsDeleteModalOpen(true);
  };

  const handleAddHealthZone = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveHealthZone = async (healthZoneData) => {
    const result = await addHealthZone(healthZoneData);
    if (result.success) {
      refetch();
    }
  };

  const handleUpdateHealthZone = async (healthZoneId, healthZoneData) => {
    const result = await updateHealthZone(healthZoneId, healthZoneData);
    if (result.success) {
      refetch();
    }
  };

  const handleConfirmDelete = async (healthZoneId) => {
    const result = await deleteHealthZone(healthZoneId);
    if (result.success) {
      refetch();
    }
  };

  const closeModals = () => {
    setIsDetailsModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsAddModalOpen(false);
    setSelectedHealthZone(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fonctionnelle':
        return 'bg-green-100 text-green-800';
      case 'Partiellement fonctionnelle':
        return 'bg-yellow-100 text-yellow-800';
      case 'Non fonctionnelle':
        return 'bg-red-100 text-red-800';
      case 'En construction':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Zones de Santé</h1>
              <p className="text-gray-600 mt-2">Gestion des zones de santé en République Démocratique du Congo</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAddHealthZone}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter Zone</span>
              </button>
              <button
                onClick={exportToPDF}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Exporter PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des zones de santé...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher une zone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterProvince}
                  onChange={(e) => setFilterProvince(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les provinces</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tous les statuts</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500 flex items-center">
                {filteredHealthZones.length} zone(s) trouvée(s)
              </div>
            </div>
          </div>
        )}

        {/* Health Zones Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHealthZones.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-400 mb-4">
                  <MapPin className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune zone de santé trouvée</h3>
                <p className="text-gray-500">Aucune zone ne correspond à vos critères de recherche.</p>
              </div>
            ) : (
              filteredHealthZones.map((zone) => (
                <div
                  key={zone.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{zone.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">Code: {zone.code}</p>
                      <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(zone.status)}`}>
                        {zone.status}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleViewDetails(zone)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditHealthZone(zone)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteHealthZone(zone)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{zone.province} - {zone.territory}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="h-4 w-4 mr-2" />
                      <span>{zone.chiefTown}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span>{zone.population?.toLocaleString() || '0'} habitants</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      <span>{zone.healthCenters || 0} centres</span>
                    </div>
                    <div className="flex items-center">
                      <Activity className="h-4 w-4 mr-2" />
                      <span>{zone.doctors || 0} médecins</span>
                    </div>
                  </div>

                  {(zone.contactPhone || zone.contactEmail) && (
                    <div className="border-t pt-3 space-y-1">
                      {zone.contactPhone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2" />
                          <span>{zone.contactPhone}</span>
                        </div>
                      )}
                      {zone.contactEmail && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="h-4 w-4 mr-2" />
                          <span>{zone.contactEmail}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Modales */}
        <HealthZoneDetailsModal
          healthZone={selectedHealthZone}
          isOpen={isDetailsModalOpen}
          onClose={closeModals}
        />

        <HealthZoneEditModal
          healthZone={selectedHealthZone}
          isOpen={isEditModalOpen}
          onClose={closeModals}
          onSave={handleUpdateHealthZone}
        />

        <HealthZoneDeleteModal
          healthZone={selectedHealthZone}
          isOpen={isDeleteModalOpen}
          onClose={closeModals}
          onConfirm={handleConfirmDelete}
        />

        <HealthZoneAddModal
          isOpen={isAddModalOpen}
          onClose={closeModals}
          onSave={handleSaveHealthZone}
        />
      </div>
    </div>
  );
};

export default HealthZonesList;
